import React, { FC, memo } from 'react'
import { FluidImage, HStack } from '../index'
import { PhoneSpecs as Phone, PhoneCondition } from '../../phones'
import { PhoneSpecs } from './phone-specs'
import { Box, Center, Flex, Tag } from '@chakra-ui/react'
import i18next from 'i18next'

export interface PhoneCardProps {
  phone: Phone
  onClick: (modelId: Phone['modelId']) => void
}

const translateMemory = (memoryName: string) => {
  const data = memoryName?.match(/(?<dataValue>\d+)(\s)?(?<dataUnit>MB|GB|TB)/i)
  if (data?.groups?.dataUnit && data?.groups?.dataValue) {
    const translatedUnit = i18next.t(`common_${data?.groups?.dataUnit}`)
    memoryName = `${data?.groups?.dataValue} ${translatedUnit}`
  }

  return memoryName
}

const showPhoneConditionTag = (condition: string) => {
  switch (condition) {
    case PhoneCondition.REFURBISHED:
      return i18next.t('like-new') as string
    case PhoneCondition.REFURBISHED_B:
      return i18next.t('very-good') as string
    case PhoneCondition.REFURBISHED_C:
      return i18next.t('good') as string
    default:
      return ''
  }
}

const managePressedKey =
  (onClick: { (modelId: number): void; (arg0: any): void }, modelId: number) => (e: { key: string }) => {
    if (e.key === 'Enter') {
      onClick(modelId)
    }
  }

/**
 * There should probably be a more generic "card" component, but I can't see a pattern, plus, some cards in the UI
 * have a hover state and others don't. This one requires a label, others don't. Can't see the pattern yet, so this
 * guy is split for readability purposes in <PhoneList />.
 */
export const PhoneCard: FC<PhoneCardProps> = memo(({ phone, onClick }) => {
  return (
    <HStack
      role="button"
      aria-label={phone.name}
      width="full"
      maxWidth={290}
      maxHeight={120}
      borderRadius="default"
      justifyContent="space-between"
      padding={4}
      marginBottom={4}
      border={2}
      borderStyle="solid"
      borderColor="grey"
      userSelect="none"
      cursor="pointer"
      marginX={1}
      _hover={{
        borderColor: 'brand',
      }}
      label={phone.name}
      onClick={() => onClick(phone.modelId)}
      onKeyPress={managePressedKey(onClick, phone.modelId)}
      position="relative"
      _focus={{
        borderColor: 'brand',
        boxShadow: '0 0 0 3px #5bce4c',
      }}
      tabIndex={0}
    >
      <Flex alignItems="center" width="full" height="100%">
        <Center width={20} height={20} flex={2}>
          <FluidImage src={`https://www.planhub.ca/img/phone/${phone.slug}.png`} alt={phone.name} />
        </Center>
        <Box flex={2}>
          <PhoneSpecs name={phone.name} brand={phone.brandName} memory={translateMemory(phone.modelMemoryName)} />
        </Box>
        <Box flex={1} position="relative" height="100%">
          {phone.modelPriceTier !== undefined &&
            phone.modelPriceTier !== null &&
            [1, 2, 3, 4, 5].includes(phone.modelPriceTier) && (
              <Tag
                colorScheme="green"
                variant="outline"
                borderWidth="1px"
                fontSize="16px"
                fontWeight="bold"
                position="absolute"
                top={0}
                right={0}
                width={'fit-content'}
                textAlign="center"
                backgroundColor="green.50"
                whiteSpace="nowrap"
                display="inline-flex"
                alignItems="center"
              >
                {i18next.t(`common_price-tier-${phone.modelPriceTier}`) as string}
              </Tag>
            )}
          {phone.modelCondition &&
            [PhoneCondition.REFURBISHED, PhoneCondition.REFURBISHED_B, PhoneCondition.REFURBISHED_C].includes(
              phone.modelCondition as PhoneCondition,
            ) && (
              <Box
                borderWidth="1px"
                borderRadius="md"
                fontSize="12px"
                fontWeight="bold"
                position="absolute"
                bottom={0}
                right={0}
                minW={'85px'}
                backgroundColor="#ffe8ff"
                color="rgb(128, 0, 128)"
                boxShadow="rgb(128, 0, 128) 0 0 0 1px inset"
                zIndex={1}
                display="inline-flex"
                whiteSpace="nowrap"
                justifyContent="center"
                alignItems="center"
                px={2}
                py={1}
              >
                {showPhoneConditionTag(phone.modelCondition) as string}
              </Box>
            )}
        </Box>
      </Flex>
    </HStack>
  )
})
